/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {FindNearbyRequestArgs} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {loadServiceStatus} from '@ion/core/src/services/Maintenance';
import {FindNearbyType} from '@ion/core/src/types/entities';
import {convertOdometer, isMetric} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import {AssetDetailsAccordion as CurrentAssetDetailsAccordion} from '@ion/dls/src/components/assetdetails/current/AssetDetailsAccordion';
import {CommandBar} from '@ion/dls/src/components/commandbar/current/CommandBar';
import Divider from '@ion/dls/src/components/divider/Divider';
import PanelComments from '@ion/dls/src/components/sections/PanelComments';
import {useCallbackWithValue} from '@ion/dls/src/hooks/useCallbackWithValue';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {SideSheetView} from '@ion/dls/src/types/types';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {cacheOnFetch} from '@ion/query/src/utils/helpers';
import {setStreetViewAsset} from '@/components/streetview/store';
import Features, {hasHistoryAccess, hasMaintanenceAccess} from '@/security/Features';
import {setPage} from '@/store/actions/nav';
import {
  findButtonStateSelector,
  showPopupSelector,
  sideSheetViewSelector
} from '@/store/selectors/tracking-selectors';
import {selectSchedule} from '../../store/actions/maintenance';
import {
  findNearby,
  launchSHR,
  setFindButtonClickStatus,
  setFindNearbyAsset,
  setFindNearbyFromItem,
  setFindNearbyPanelActive,
  setFindNearbyPopup,
  setFindNearbyToggle,
  setSideSheetView
} from '../../store/actions/tracking';
import changePage, {getRoute, Route} from '../RouteDefinitions';
import {getFindNearbyAsset} from '../tracking2/helpers';
import useViewAssetHistory from './hooks/history/useViewAssetHistory';
import {useStyles} from './sidesheetdetails/accordionStyles';
import AssetCardListContent from './AssetCardListContent';
import {AssetDetailOverviewPanelProps} from './AssetDetailOverviewPanel';
import {
  AssetDetailsSection,
  resetAssetDetailsSections,
  useRegisteredAccordion
} from './assetDetailsAccordionStore';
import FindNearByPopupContent, {OnFindNearByProps} from './FindNearByPopupContent';
import {getAssetSummaryCardProps} from './getAssetSummaryCardProps';
import {commentsLabels, getAssetId} from './trackingUtils';

const AssetDetailsAccordion = (props: AssetDetailOverviewPanelProps) => {
  const [isIconDisabled, setIsIconDisabled] = useState(true);
  const isMounted                           = useHasMounted();
  const viewHistory                         = useViewAssetHistory();
  const classes                             = useStyles();
  const dispatch                            = useDispatch();
  const {isDesktop, isPhone}                = useMedia();
  const {asset, trackingV2}                 = props;
  const showFindNearByPopup                 = useSelector(showPopupSelector);
  const sideSheetView                       = useSelector(sideSheetViewSelector);
  const findButtonState                     = useSelector(findButtonStateSelector);

  const setFindNearbyActions = useCallback(() => {
    dispatch(setFindNearbyFromItem(getFindNearbyAsset(asset.getDevice?.() as any)));
    dispatch(setFindNearbyPanelActive(true));
  }, [asset]);

  // Find Nearby visibility based on showFindNearByPopup and findButtonState states
  const showFindNearBy = useMemo(() => {
    if (isDesktop || isPhone) {
      return isPhone ? showFindNearByPopup && !findButtonState : showFindNearByPopup;
    }
    return false;
  }, [isPhone, isDesktop, showFindNearByPopup, findButtonState]);

  /**
 * By using getServiceStatus() function, we load serviceStatus of individual asset and based
 * on values we either enable or disable the "Maintenance Schedule" icon
 * in PanelComments Component
 */
  const getServiceStatus = async(asset: Device) => {
    try {
      const assetId = getAssetId(asset);
      const serviceStatus = await cacheOnFetch({
        queryKey : createKey(QueryKey.ASSET_SERVICE_STATUS, assetId),
        queryFn  : async() => await loadServiceStatus(assetId, true)
      });
      setIsIconDisabled(
        !((serviceStatus.data.count > 0 && serviceStatus.data.rows.length > 0))
      );
    } catch (err) {
      setIsIconDisabled(true);
      error('Error occurred while determining whether the Maintenance Schedule CTA should be disabled:', err);
    }
  };
  useEffect(() => {
    if (isMounted && !asset?.isScDevice()) {
      getServiceStatus(asset);
    }
  }, [isMounted, asset]);

  const maintenanceSchedule = useCallback(function() {
    dispatch(selectSchedule(asset.getDevice?.()));
    dispatch(setPage(getRoute(Route.MAINTENANCE)));
    changePage(Route.MAINTENANCE);
  }, [asset]);

  const findNearest = useCallback(function() {
    isPhone && dispatch(setFindButtonClickStatus(false));
    dispatch(setFindNearbyAsset(asset.getDevice?.()));
    !trackingV2 ? dispatch(setFindNearbyPopup(true)) : setFindNearbyActions();
    dispatch(setFindNearbyToggle());
  }, [isPhone, asset, trackingV2]);

  const alignIcon = useCallback(function() {
    return isPhone ? 'stack' : 'left';
  }, [isPhone]);

  const onCancelFindNearBy = useCallback(function() {
    dispatch(setFindNearbyPopup(false));
  }, []);

  const onFindNearest = useCallback(function({
    findNearbyChip,
    uom,
    distance,
    label
  }: OnFindNearByProps) {
    findNearbyChip.map((chip) => {
      if (chip?.isSelected) {
        const dist = isMetric(uom) ? convertOdometer(distance, uom, Unit.US, true) : distance;
        let {latitude, longitude} = asset?.getDevice();
        if (!latitude && !longitude) {
          const {geometry} = asset?.getDevice() || {};
          if (geometry) {
            const {type, centroid} = geometry;
            if (type === 'polygon') {
              latitude  = centroid.latitude;
              longitude = centroid.longitude;
            }
          }
        }
        const params: FindNearbyRequestArgs = {
          latitude,
          longitude,
          label,
          miles : dist
        };
        dispatch(findNearby({
          ...params,
          findType : FindNearbyType.asset
        }) as any);
      }
    });
    if (sideSheetView !== SideSheetView.detailed) {
      dispatch(setSideSheetView(SideSheetView.detailed));
    }
  }, [sideSheetView]);

  const viewSHRHistory = useCallback(() => {
    const selectedDevice = asset?.getDevice();
    const date = new Date(selectedDevice?.eventTime);
    dispatch(launchSHR({selectedDevice, date}) as any);
  }, [asset?.getDevice()]);

  const {
    historyEnabled,
    viewHistoryClick,
    viewMaintenanceScheduleClick
  } = useMemo(() => ({
    historyEnabled               : hasHistoryAccess(),
    viewHistoryClick             : asset.isScDevice?.() ? viewSHRHistory : viewHistory,
    viewMaintenanceScheduleClick : asset.isScDevice?.() ? null : maintenanceSchedule
  }), [asset.isScDevice?.(), viewSHRHistory, viewHistory, maintenanceSchedule]);

  // CommandBar wires this directly as a DOM onClick, so it's called with
  // the MouseEvent as the first arg - viewHistoryClick's underlying
  // viewAssetHistory(asset) takes an OPTIONAL asset and falls back to the
  // selected asset only when none is passed, so forwarding the event
  // straight through (as onClick={viewHistoryClick} would) makes it treat
  // the event itself as the asset, spreading DOM/React internals (target,
  // view) into Redux state and eventually into a query cache key. Legacy's
  // PanelComments avoids this the same way, via this same shared hook ...
  const onCommandBarViewHistory = useCallbackWithValue(viewHistoryClick, asset?.getDevice());

  // only computed for the current theme - the legacy AssetCardListContent
  // below is left entirely as-is otherwise, matching AssetCard.tsx's own
  // isCurrentTheme()-gated summaryCardProps computation. The Driver row is
  // new to this card in the redesign (legacy AssetCardListContent has no
  // driver row here at all) - with no driver details destination yet, no
  // onDriverClick is passed, so the driver shows as plain text, not a link ...
  const summaryCardProps = useMemo(() => (
    isCurrentTheme() ? getAssetSummaryCardProps(asset) : null
  ), [asset]);

  // registers this section with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // current theme only, the legacy Accordion below stays uncontrolled ...
  const {
    expanded : isDetailsExpanded,
    onExpand : onDetailsExpand
  } = useRegisteredAccordion(AssetDetailsSection.assetDetails, true, isCurrentTheme());

  // the drawer stays open from one asset to the next, so each section is put
  // back the way it started rather than left as it was for the last asset ...
  const selectedAssetId = getAssetId(asset);
  useEffect(() => {
    if (isCurrentTheme()) {
      resetAssetDetailsSections();
    }
  }, [selectedAssetId]);

  return isCurrentTheme() ? (
    <>
      <CurrentAssetDetailsAccordion location={summaryCardProps?.location}
        group={summaryCardProps?.group}
        route={summaryCardProps?.route}
        driver={summaryCardProps?.driver}
        onDriverClick={summaryCardProps?.onDriverClick}
        expanded={isDetailsExpanded}
        onExpand={onDetailsExpand}
        defaultExpanded />
      {(asset.isCVSAsset() || asset.isScDevice?.()) && (
        <>
          <CommandBar assetData={asset?.getDevice()}
            onFindNearby={findNearest}
            onMaintenance={hasMaintanenceAccess() ? viewMaintenanceScheduleClick : undefined}
            onViewHistory={historyEnabled ? onCommandBarViewHistory : undefined}
            onStreetView={trackingV2 ? setStreetViewAsset : undefined} />
          {showFindNearBy && !trackingV2 && (
            <FindNearByPopupContent asset={asset?.getDevice()}
              onFindButtonClick={onFindNearest}
              onCancel={onCancelFindNearBy}/>
          )}
        </>
      )}
    </>
  ) : (
    <div className={classes.accordionWrapper}>
      <Accordion className={classes.assetDetailAccordion}
        header={<div className={classes.assetDetails}>
          {translate(`tracking:assetDetail.details.${asset.isScDevice?.() ? 'deviceHeader' : 'header'}`)}
        </div>}
        details={
          <div>
            <div className={classes.assetCardListContentWrapper} style={{paddingBottom: 15}}>
              <AssetCardListContent {...props} />
            </div>
            {(asset.isCVSAsset() || asset.isScDevice?.()) && (
              <>
                <Divider />
                <PanelComments findNearbyClick={findNearest}
                  viewHistoryClick={historyEnabled ? viewHistoryClick : null}
                  viewMaintenanceScheduleClick={hasMaintanenceAccess() ? viewMaintenanceScheduleClick : null}
                  streetViewClick={trackingV2 ? setStreetViewAsset : null}
                  labels={commentsLabels()}
                  assetData={asset?.getDevice()}
                  iconDirection={alignIcon()}
                  disabled={isIconDisabled} />
                {showFindNearBy && !trackingV2 && (
                  <FindNearByPopupContent asset={asset?.getDevice()}
                    onFindButtonClick={onFindNearest}
                    onCancel={onCancelFindNearBy}/>
                )}
              </>
            )}
          </div>
        }
        padding={'15px 0 0'}
        borderBottom={'none'}
        defaultExpanded={true}
        isCondensedVariant />
    </div>
  );
};

export default AssetDetailsAccordion;
